import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Upload, FileText, Check, ExternalLink } from "lucide-react";

const FIELDS = [
  ["address", "Address"], ["mls_number", "MLS #"], ["status", "Status"], ["list_price", "List price", true],
  ["tenure", "Tenure (Fee Simple / Leasehold)"], ["property_type", "Property type"], ["beds", "Bedrooms"], ["baths", "Baths"],
  ["sqft", "Living area (sq ft)"], ["parking", "Parking"], ["year_built", "Year built"], ["hoa_fee", "HOA / maintenance fee"],
  ["hoa_includes", "HOA includes"], ["taxes", "Taxes"], ["flooring", "Flooring"], ["ac", "Air conditioning"],
  ["amenities", "Amenities"], ["inclusions", "Inclusions"], ["schools", "Schools"], ["flood_zone", "Flood zone"],
];

export function MlsUpload({ clientId }) {
  const qc = useQueryClient();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploadId, setUploadId] = useState(null);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/agent/clients/${clientId}/mls-upload`, fd);
      setUploadId(data.upload_id);
      const e = data.extracted || {};
      setF({
        title: e.title || "", address: e.address || "", mls_number: e.mls_number || "", status: e.status || "",
        list_price: e.list_price ?? "", tenure: e.tenure || "", property_type: e.property_type || "", beds: e.beds || "",
        baths: e.baths || "", sqft: e.sqft || "", parking: e.parking || "", year_built: e.year_built || "", hoa_fee: e.hoa_fee || "",
        hoa_includes: e.hoa_includes || "", taxes: e.taxes || "", flooring: e.flooring || "", ac: e.ac || "", amenities: e.amenities || "",
        inclusions: e.inclusions || "", schools: e.schools || "", flood_zone: e.flood_zone || "", public_remarks: e.public_remarks || "",
        criteria_meets: (e.criteria_meets || []).join("\n"), criteria_not_meets: (e.criteria_not_meets || []).join("\n"), agent_verify: (e.agent_verify || []).join("\n"),
      });
      toast.success("Sheet read — review the fields below before saving");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    setBusy(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const lines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
      const payload = {
        ...f, list_price: f.list_price !== "" && f.list_price != null ? Number(f.list_price) : null,
        criteria_meets: lines(f.criteria_meets), criteria_not_meets: lines(f.criteria_not_meets), agent_verify: lines(f.agent_verify),
      };
      await api.post(`/agent/clients/${clientId}/mls-upload/${uploadId}/approve`, payload);
      qc.invalidateQueries({ queryKey: ["client", clientId] });
      toast.success("Property added to this client's portal");
      setF(null); setUploadId(null); setFile(null);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
    setSaving(false);
  };

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pdfUrl = uploadId ? `${process.env.REACT_APP_BACKEND_URL}/api/agent/mls-file/${uploadId}` : null;

  if (!f) {
    return (
      <Card className="p-6 rounded-2xl max-w-xl" data-testid="mls-upload-card">
        <h3 className="font-heading font-semibold mb-1">Upload an MLS sheet</h3>
        <p className="text-sm text-muted-foreground mb-4">Upload a HiCentral "Residential Client Full" PDF. The app reads it and pre-fills the property for your review — nothing reaches the client until you save.</p>
        <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="mls-file-input" className="block text-sm mb-4" />
        <Button onClick={upload} disabled={!file || busy} data-testid="mls-upload-btn" className="rounded-full gap-2">
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Reading the sheet…</> : <><Upload className="h-4 w-4" /> Read PDF</>}
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-5 max-w-3xl" data-testid="mls-review">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading font-semibold">Review extracted details</h3>
          <p className="text-xs text-muted-foreground max-w-xl">Correct anything before saving. A blank field means it wasn't on the sheet — don't guess. Source: HiCentral MLS — deemed reliable but not guaranteed.</p>
        </div>
        {pdfUrl && <a href={pdfUrl} target="_blank" rel="noreferrer" className="text-sm text-primary inline-flex items-center gap-1 hover:underline shrink-0" data-testid="mls-view-pdf"><FileText className="h-4 w-4" /> View original PDF <ExternalLink className="h-3 w-3" /></a>}
      </div>

      <Card className="p-5 rounded-2xl grid sm:grid-cols-2 gap-4">
        {FIELDS.map(([k, label, num]) => (
          <div key={k} className="space-y-1.5">
            <Label className="text-xs">{label}</Label>
            <Input type={num ? "number" : "text"} value={f[k]} onChange={set(k)} data-testid={`mls-field-${k}`} className="h-10 rounded-xl" />
          </div>
        ))}
        <div className="sm:col-span-2 space-y-1.5">
          <Label className="text-xs">Public remarks</Label>
          <Textarea value={f.public_remarks} onChange={set("public_remarks")} data-testid="mls-field-public_remarks" className="rounded-xl" />
        </div>
      </Card>

      <Card className="p-5 rounded-2xl space-y-4">
        <p className="text-sm font-semibold">Criteria match <span className="font-normal text-muted-foreground">(one per line)</span></p>
        <div className="space-y-1.5"><Label className="text-xs text-status-complete">Meets — buyer sees this</Label><Textarea value={f.criteria_meets} onChange={set("criteria_meets")} data-testid="mls-meets" className="rounded-xl" /></div>
        <div className="space-y-1.5"><Label className="text-xs text-status-warning">Doesn't meet — buyer sees this</Label><Textarea value={f.criteria_not_meets} onChange={set("criteria_not_meets")} data-testid="mls-not-meets" className="rounded-xl" /></div>
        <div className="space-y-1.5"><Label className="text-xs text-primary">To verify — agent only (hidden from buyer)</Label><Textarea value={f.agent_verify} onChange={set("agent_verify")} data-testid="mls-verify" className="rounded-xl" /></div>
      </Card>

      <div className="flex gap-2">
        <Button onClick={save} disabled={saving} data-testid="mls-save-btn" className="rounded-full gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save & add to portal</Button>
        <Button variant="ghost" onClick={() => { setF(null); setUploadId(null); setFile(null); }} data-testid="mls-cancel-btn">Cancel</Button>
      </div>
    </div>
  );
}
